import { LoadingIndicator, StatusBadge, Surface } from './ui/Primitives';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './PhoneSection.css';
import BillingLifecycle from './BillingLifecycle';

const money = cents => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
const minutes = seconds => (seconds / 60).toLocaleString('en-US', { maximumFractionDigits: 1 });
function formatPhoneNumber(value) {
  const cleaned = String(value || '').replace(/\D/g, '');
  const match = cleaned.match(/^(\d{1})(\d{3})(\d{3})(\d{4})$/);
  return match ? `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}` : value || 'Number setup in progress';
}

function PhoneSection({ organization }) {
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState('');
  const phoneNumber = organization?.provisioned_number;
  const usage = subscription?.usage;
  const usesCallDuration = usage?.usage_basis === 'call_duration';
  const usageLabel = usesCallDuration ? 'call minutes' : 'AI minutes';
  const isComped = subscription?.billing_access === 'comped';
  const isOnline = ['active', 'trialing'].includes(subscription?.status_code);
  const usageLimitReached = !isComped && !usesCallDuration && usage && (
    (usage.trial && usage.used_seconds >= usage.included_seconds) ||
    (!usage.trial && usage.overage_limit_cents !== null && usage.used_seconds >= usage.included_seconds + Math.floor(usage.overage_limit_cents * 60 / usage.overage_cents_per_minute))
  );
  const showExpandedBilling = Boolean(subscription) && (!isOnline || Boolean(usageLimitReached));
  const canManage = subscription?.can_manage_billing === true;

  async function refresh() {
    const token = localStorage.getItem('jwt_authorization');
    if (!token) return;
    const response = await axios.post(`${process.env.REACT_APP_BACKEND_HOST}/subscription`, { jwt_token: token });
    setSubscription(response.data);
  }
  useEffect(() => {
    const load = () => refresh().catch(() => setError('Unable to load billing status. Please refresh the page.'));
    load();
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, []);


  return (
    <Surface className="phone-info-section">
      <div className="phone-line">
        <div className="phone-status-heading"><span className="say-eyebrow">AI receptionist line</span><StatusBadge status={usageLimitReached ? 'danger' : isOnline ? 'live' : subscription ? 'danger' : 'processing'}>{usageLimitReached ? 'Usage limit reached' : isOnline ? 'Online' : subscription ? 'Action needed' : <LoadingIndicator label="Checking status…" />}</StatusBadge></div>
        <div className="phone-number-row">
          <div className="phone-status-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M7.3 3H4.8A1.8 1.8 0 0 0 3 4.8C3 13.75 10.25 21 19.2 21a1.8 1.8 0 0 0 1.8-1.8v-2.5l-4.4-1.1-1.1 2.2a13.6 13.6 0 0 1-9.3-9.3l2.2-1.1L7.3 3Z" /></svg>
          </div>
          <div className="phone-info-primary">
            <strong data-say-id="ai-phone-number">{formatPhoneNumber(phoneNumber)}</strong>
            <p>{phoneNumber ? 'Forward calls to this number when you want Sayable to answer.' : 'Your receptionist number will appear here as soon as setup is complete.'}</p>
            {subscription && !isOnline && <small className="phone-offline-note">Your receptionist is offline until billing is resolved.</small>}
            {canManage && !isComped && <BillingLifecycle />}
          </div>
        </div>
      </div>
      <div className="phone-subscription">
        <span>Plan</span><strong>{subscription?.name || <LoadingIndicator label="Loading plan…" />}</strong>
        {isComped && <small className="phone-comped-status">Comped · all usage included</small>}
        {usage && <div className="phone-usage">
          {showExpandedBilling && !isComped && usage.monthly_cents != null && usage.overage_cents_per_minute != null && <p>{money(usage.monthly_cents)}/month per AI receptionist · {money(usage.overage_cents_per_minute)}/additional minute</p>}
          <p>{minutes(usage.used_seconds)} / {minutes(usage.included_seconds)} {usageLabel} used {usage.trial ? 'in your trial' : 'this billing month'}</p>
          {showExpandedBilling && usesCallDuration && <p>Based on recorded call duration, including transfers.</p>}
          <progress aria-label={`Included ${usageLabel} used`} max={usage.included_seconds} value={Math.min(usage.used_seconds, usage.included_seconds)} />
          {showExpandedBilling && <p>{usage.trial ? 'Trial ends' : 'Billing period ends'} {new Date(usage.period_end * 1000).toLocaleDateString()}</p>}
          {showExpandedBilling && !isComped && usage.estimated_overage_cents != null && <p>Estimated overage: {money(usage.estimated_overage_cents)} · limit: {usage.overage_limit_cents === null ? 'Unlimited' : money(usage.overage_limit_cents)}</p>}
          {!isComped && usage.near_allowance && <p role="status">{usesCallDuration ? (usage.at_allowance ? 'Recorded call time has reached your included minutes.' : 'You have used at least 80% of your included call minutes.') : usage.trial ? (usage.at_allowance ? 'Your trial minutes are used. Add a payment method through Account settings → Manage subscription to continue automatically when the trial ends.' : 'You have used at least 80% of your trial minutes.') : (usage.at_allowance ? 'Your included minutes are used. Additional AI time is billed at your plan rate, within your spending limit.' : 'You have used at least 80% of your included minutes.')}</p>}
        </div>}
        {error && <p role="alert">{error}</p>}
      </div>
    </Surface>
  );
}

export default PhoneSection;
